/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import React, { useContext, useEffect } from "react";
import { useRouter } from "next/router";
import Image from "next/image";
import cx from "classnames";

import {
  ImageHelper,
  useGetMedia,
  ThemeContext,
  channelTagsFromMedia,
  CellType,
  ComponentType,
  Orientation,
  IListComponentModel,
  GridComponentType,
  IConfigurationModel,
  IMediaModel,
  EmptyObject,
} from "@nf/common";
import {
  useMediaDetailsMediaSelector,
  useMediaDetailsActionsSelector,
  IMediaDetailsParams,
} from "../MediaDetailsScreen";

import LeaveArrowMobile from "resources/icons/leave-cross.svg";
import LeaveArrow from "resources/icons/back.svg";

const GridComponent = dynamic<IGridComponentOwnProps>(() =>
  import(
    "components/Configuration/ListComponent/components/GridComponent"
  ).then((mod) => mod.GridComponent)
);
const MediaDetailsBottomContent = dynamic<IMediaDetailsBottomContentProps>(() =>
  import("components/Media/MediaDetailsBottomContent").then(
    (mod) => mod.MediaDetailsBottomContent
  )
);
const MediaDetailsButton = dynamic<IMediaDetailsButtonProps>(() =>
  import("components/Media/MediaDetailsButton").then(
    (mod) => mod.MediaDetailsButton
  )
);
const MediaDetailsError = dynamic<EmptyObject>(() =>
  import("components/Media/MediaDetailsErrorModal").then(
    (mod) => mod.MediaDetailsError
  )
);
const PlayInfo = dynamic<PlayInfoProps>(() =>
  import("components/PlayInfo").then((mod) => mod.PlayInfo)
);
const MediaSEO = dynamic<IMediaSeoProps>(() =>
  import("components/SEO").then((mod) => mod.MediaSEO)
);

import styles from "./CollectionScreen.module.scss";
import {
  useGoBackOrHome,
  useScrollToTopOnMount,
  useSyncCompleted,
} from "../../hooks";
import dynamic from "next/dynamic";
import {
  IMediaDetailsBottomContentProps,
  IMediaDetailsButtonProps,
  IMediaSeoProps,
  MediaSEOField,
} from "../../components";
import { IGridComponentOwnProps } from "components/Configuration/ListComponent/components/GridComponent/GridComponent";
import { PlayInfoProps } from "components/PlayInfo";

export interface ICollectionScreenProps {
  media?: IMediaModel;
  configuration?: IConfigurationModel;
}

const CollectionScreen = ({
  media: mediaProps,
  configuration,
}: ICollectionScreenProps) => {
  const mediaState = useMediaDetailsMediaSelector();
  const media = mediaState || mediaProps;
  const channelContext = useContext(ThemeContext);
  const { isLoading, error } = useMediaDetailsActionsSelector();
  const isAuthProcessCompleted = useSyncCompleted();
  const getMedia = useGetMedia();
  const router = useRouter();
  const params = router.query as IMediaDetailsParams;
  const frameImageUrl = ImageHelper.getHeroAssetDetailsImageUrl(
    mediaProps?.Images || media?.Images
  );
  const goBackOrHome = useGoBackOrHome();

  useScrollToTopOnMount?.();

  useEffect(() => {
    if (params.id && !mediaState) {
      getMedia({
        MediaId: params.id,
        OnlyPublished: false,
      });
    }
  }, [params.id]);

  useEffect(() => {
    if (media && channelContext.onBrandingChange) {
      channelContext.onBrandingChange(channelTagsFromMedia(media));
    }
    return () => {
      // Restoring default branding
      channelContext?.onBrandingChange?.();
    };
  }, [media]);

  if (!isLoading && error) {
    return <MediaDetailsError />;
  }

  const renderCollectionList = () => {
    const eventsListModel: IListComponentModel = {
      CellType: CellType.Cover,
      ComponentTypeCode: ComponentType.List,
      Orientation: Orientation.Grid,
      MediaList: media?.Media,
      QueryParams: {
        collection: media?.Id,
      },
    };

    return (
      media && (
        <GridComponent
          className={styles.gridContainer}
          columns={6}
          rows={media.Media ? media.Media.length : 0}
          component={eventsListModel}
          gridComponentType={GridComponentType.MovieDetails}
        />
      )
    );
  };

  return (
    <div className={styles.collection}>
      {media && frameImageUrl && (
        <>
          <MediaSEO media={media} configuration={configuration} />

          <div className={styles.background}>
            <Image
              src={frameImageUrl}
              alt={mediaProps?.Title}
              layout="fill"
              objectFit="cover"
              priority
            />

            {isAuthProcessCompleted && <PlayInfo isHighlight media={media} />}

            <MediaDetailsButton
              className={cx(styles.exitIcon)}
              icon={<LeaveArrowMobile />}
              onClick={goBackOrHome}
            />
          </div>
          <div className={styles.details}>
            <div className={styles.top}>
              <MediaDetailsButton
                className={styles.backBtn}
                icon={<LeaveArrow />}
                onClick={goBackOrHome}
              />
              <h1>{media.Title}</h1>
            </div>
            <MediaDetailsBottomContent
              className={styles.bottom}
              descrClass={styles.description}
              media={media}
              isCollection
            />
          </div>
          <div className={styles.assetsContainer}>{renderCollectionList()}</div>
          <div className={styles.assetsContainer}>
            <MediaSEOField media={media} className={styles.article} />
          </div>
        </>
      )}
    </div>
  );
};

export default CollectionScreen;
